// Asset management service - Updated to match Postman Collection
import { getCurrentConfig, getApiScope } from '../config';
import { API_ENDPOINTS, DEFAULT_HEADERS } from '../config/constants';

// Asset interface based on your actual API response
export interface Asset {
  asset_id: number;
  section_id: number;
  section_name: string;
  checkpoint_name: string;
  terminal_name: string;
  unit_name: string;
  description: string;
  purpose: string;
  isOpen: boolean;
  created_date: string;
  is_active: boolean;
}

// Legacy interface for compatibility with existing components
export interface AssetData {
  asset_id: number;
  section_id: number;
  asset_name: string; // Will map to unit_name in API
  asset_code: string;
  is_active: boolean;
  is_open: boolean;
  purpose: string;
  description?: string;
  created_date: string;
  updated_date?: string;
  updated_by?: string;
}

export interface AssetFormData {
  section_id: number;
  unit_name: string;
  description?: string;
  purpose: string;
}

// Legacy form data interface
export interface LegacyAssetFormData {
  section_id: number;
  asset_name: string;
  purpose?: string;
  description?: string;
}

class AssetService {
  private getBaseURL(): string {
    return getCurrentConfig().apiBaseUrl;
  }

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const baseUrl = this.getBaseURL();
    
    // Fix double slash issue - simple URL construction
    let url = baseUrl + endpoint;
    url = url.replace(/\/+/g, '/'); // Replace multiple slashes with single slash
    url = url.replace(':/', '://'); // Fix protocol if we accidentally broke it
    
    // Get access token if available (same pattern as api.ts)
    let token: string | null = null;
    try {
      const config = getCurrentConfig();
      
      if (config.auth.enableAuthentication) {
        const { msalInstance } = await import('../contexts/AuthContext');
        const accounts = msalInstance.getAllAccounts();
        
        if (accounts.length > 0) {
          const tokenResponse = await msalInstance.acquireTokenSilent({
            scopes: [getApiScope()], // Use scope from .env
            account: accounts[0]
          });
          token = tokenResponse.accessToken;
        }
      }
    } catch (error) {
      // Silent fail for token acquisition
    }
    
    const headers: Record<string, string> = {
      ...DEFAULT_HEADERS,
    };
    
    // Add any additional headers from options
    if (options?.headers) {
      Object.entries(options.headers).forEach(([key, value]) => {
        if (typeof value === 'string') {
          headers[key] = value;
        }
      });
    }
    
    // Add authorization header if token is available
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(url, {
      headers,
      ...options,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Convert API response to legacy format for compatibility
  private mapToLegacyFormat(asset: Asset): AssetData {
    return {
      asset_id: asset.asset_id,
      section_id: asset.section_id,
      asset_name: asset.unit_name, // Map unit_name to asset_name for frontend
      asset_code: `AST-${asset.asset_id.toString().padStart(3, '0')}`,
      is_active: asset.is_active,
      is_open: asset.isOpen,
      purpose: asset.purpose,
      description: asset.description,
      created_date: asset.created_date,
      updated_date: asset.created_date,
      updated_by: 'system'
    };
  }

  // Convert legacy form data to API format
  private mapLegacyFormData(data: LegacyAssetFormData): AssetFormData {
    const apiData: any = {
      section_id: data.section_id,
      unit_name: data.asset_name, // Map asset_name to unit_name for API
      description: data.description
    };
    
    // Add purpose only if provided
    if (data.purpose && data.purpose.trim()) {
      apiData.purpose = data.purpose;
    }
    
    return apiData;
  }

  // Get all assets (legacy method name)
  async getAllAssets(): Promise<AssetData[]> {
    const response = await this.request<{data: {assets: Asset[]}} | Asset[]>(API_ENDPOINTS.ASSETS);
    let assets: Asset[];
    
    if ('data' in response && response.data && 'assets' in response.data) {
      // Handle nested structure: response.data.assets
      assets = response.data.assets;
    } else if ('data' in response && Array.isArray(response.data)) {
      // Handle structure: response.data (direct array)
      assets = response.data;
    } else if (Array.isArray(response)) {
      // Handle direct array response
      assets = response;
    } else {
      assets = [];
    }
    
    return assets.map(asset => this.mapToLegacyFormat(asset));
  }

  // Get all assets (new method name)
  async getAssets(): Promise<Asset[]> {
    const response = await this.request<{data: {assets: Asset[]}} | Asset[]>(API_ENDPOINTS.ASSETS);
    
    if ('data' in response && response.data && 'assets' in response.data) {
      return response.data.assets;
    } else if ('data' in response && Array.isArray(response.data)) {
      return response.data;
    } else if (Array.isArray(response)) {
      return response;
    } else {
      return [];
    }
  }

  // Get asset by ID
  async getAsset(id: number): Promise<Asset> {
    const response = await this.request<{data: Asset} | Asset>(API_ENDPOINTS.ASSET_BY_ID(id));
    return 'data' in response ? response.data : response;
  }

  // Create new asset (legacy format)
  async createAsset(data: LegacyAssetFormData): Promise<AssetData> {
    const apiData = this.mapLegacyFormData(data);
    const response = await this.request<{data: Asset} | Asset>(API_ENDPOINTS.ASSETS, {
      method: 'POST',
      body: JSON.stringify(apiData),
    });
    const asset = 'data' in response ? response.data : response;
    return this.mapToLegacyFormat(asset);
  }

  // Update asset (legacy format)
  async updateAsset(id: number, data: Partial<LegacyAssetFormData>): Promise<AssetData> {
    const apiData = this.mapLegacyFormData(data as LegacyAssetFormData);
    const response = await this.request<{data: Asset} | Asset>(API_ENDPOINTS.ASSET_BY_ID(id), {
      method: 'PUT',
      body: JSON.stringify(apiData),
    });
    const asset = 'data' in response ? response.data : response;
    return this.mapToLegacyFormat(asset);
  }

  // Delete asset
  async deleteAsset(id: number): Promise<{ message: string }> {
    return this.request<{ message: string }>(API_ENDPOINTS.ASSET_BY_ID(id), {
      method: 'DELETE',
    });
  }

  // Validation methods for legacy compatibility
  validateAssetName(name: string): { isValid: boolean; message?: string } {
    if (!name || name.trim().length < 2) {
      return { isValid: false, message: 'Asset name must be at least 2 characters long' };
    }
    if (name.length > 100) {
      return { isValid: false, message: 'Asset name must be less than 100 characters' };
    }
    return { isValid: true };
  }

  validateAssetCode(code: string): { isValid: boolean; message?: string } {
    if (!code || code.trim().length < 3) {
      return { isValid: false, message: 'Asset code must be at least 3 characters long' };
    }
    if (!/^[A-Z0-9-]+$/i.test(code)) {
      return { isValid: false, message: 'Asset code can only contain letters, numbers, and hyphens' };
    }
    return { isValid: true };
  }

  validatePurpose(purpose: string): { isValid: boolean; message?: string } {
    if (!purpose || purpose.trim().length < 5) {
      return { isValid: false, message: 'Purpose must be at least 5 characters long' };
    }
    return { isValid: true };
  }

  validateDescription(description?: string): { isValid: boolean; message?: string } {
    if (description && description.length > 500) {
      return { isValid: false, message: 'Description must be less than 500 characters' };
    }
    return { isValid: true };
  }
}

// Export singleton instance
export const assetService = new AssetService();
export default assetService;
